import React, { useState } from "react";
import { cls } from "cls";
import { Icon } from "@clarkinc/zest-design-system/ui";
import { VariationGroup, VariationGroupItem, VariationMembership } from "./ProductDetailPageResult";
import ThumbnailVariation from "./ThumbnailVariation";
import variationAnalyticsClickTracking from "../analytics/PdpPageTracking/variationAnalyticsClickTracking";
import DisplayTypes from "./DisplayTypes";
import VariationTile from "./components/VariationTile";
import { getDivideByQuantityPricePerPackagingItem } from "./components/VariationTile/shared/utils";
import { User, withUserAndChat } from "~/common/Store/connectUserAndChat";
import getLowestPrice from "./Pricing/getLowestPrice";
interface ProductVariationsProps {
  itemNumber: string;
  variationMembership?: VariationMembership;
  isVirtualGroupMaster: boolean;
  user: User;
}
const variationHeading = (variationGroup: VariationGroup, itemNumber: string, activeItemNumber: string) => {
  const variationText = variationGroup.variationGroupItems.find(item => item.itemNumber === activeItemNumber)?.variationText ?? "";
  if (variationGroup.optionName !== "Quantity") {
    const selectedItem = variationGroup.variationGroupItems.find(item => {
      return item.itemNumber === itemNumber;
    });
    return <h2 data-testid="productVariationsHeading" id="productVariationsHeading" className="mb-1 text-sm">
        {`${variationGroup.optionName}: `}
        <span className="font-normal">{selectedItem?.variationText}</span>
      </h2>;
  }
  return <h2 data-testid="productVariationsHeading" id="productVariationsHeading" className="mb-1 text-sm" data-sentry-component="variationHeading" data-sentry-source-file="ProductVariations.tsx">
      {variationGroup.optionName}:
      <span className="font-normal"> {variationText}</span>
    </h2>;
};
const ProductVariations = ({
  itemNumber,
  variationMembership,
  isVirtualGroupMaster,
  user
}: ProductVariationsProps) => {
  const lowerCaseItemNumber = itemNumber.toLowerCase();
  const [activeItemNumber, setActiveItemNumber] = useState(lowerCaseItemNumber);
  const sortGroupItems = (group: VariationGroup, a: VariationGroupItem, b: VariationGroupItem) => {
    if (group.isSorted) {
      if (a.itemOrderBy < b.itemOrderBy) {
        return -1;
      }
      if (a.itemOrderBy > b.itemOrderBy) {
        return 1;
      }
      return 0;
    }
    return a.displayText.toLowerCase().localeCompare(b.displayText.toLowerCase());
  };
  const getHrefTarget = (groupItem: VariationGroupItem, isSelected: boolean) => {
    if (isSelected) {
      return "#";
    }
    if (isVirtualGroupMaster) {
      return groupItem.groupingMasterLink ?? groupItem.seoLink;
    }
    return groupItem.seoLink;
  };
  const getTextClass = (isSelected: boolean, isInStock: boolean) => {
    if (isSelected) {
      return "text-white font-semibold";
    }
    if (isInStock) {
      return "text-green-500 font-semibold var-in-stock";
    }
    return "text-gray-600 font-normal var-out-of-stock";
  };
  const getListClass = (group: VariationGroup) => {
    if (group.optionName === "Quantity") {
      return "grid grid-flow-row grid-cols-3 lt:grid-cols-2 [@media(min-width:1150px)]:grid-cols-3 auto-rows-min gap-2";
    }
    return "nav nav-pills nav-stacked";
  };
  const getNonThumbnailVariationItem = (groupItem: VariationGroupItem, isSelected: boolean, isQuantity: boolean, divideByQuantityItem: {
    itemNumber: string;
    divideByLowestQuantity: number | null;
    minUnit: string;
  }, setActiveItemNumber: React.Dispatch<React.SetStateAction<string>>, user: User) => {
    const displayPrice = groupItem.productPrice && getLowestPrice(groupItem.productPrice, groupItem.showBelowMinimumAdvertisedPriceForLoggedInCustomers, groupItem.showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers, user, false);
    return <>
        {isQuantity ? <li key={`${groupItem.itemNumber}-${groupItem.variationText}-quantity`} data-testid={`variationItem-${groupItem.itemNumber}`}>
            <VariationTile.Quantity key={groupItem.itemNumber} link={getHrefTarget(groupItem, isSelected)} price={displayPrice} itemNumber={groupItem.itemNumber} currentSelectedItemNumber={lowerCaseItemNumber} pricePerUom={divideByQuantityItem?.divideByLowestQuantity ?? 0} unitOfMeasure={divideByQuantityItem?.minUnit ?? ""} variationText={groupItem.variationText} isPlusEligible={groupItem.isWebstaurantPlusEligible} setActiveItemNumber={setActiveItemNumber} analyticsClickTracking={variationAnalyticsClickTracking ?? undefined} seoLink={groupItem.seoLink} />
          </li> : <li className={cls("text-sm mb-0 pt-1", isSelected ? "var-selected" : "")} key={groupItem.itemNumber}>
            <a href={getHrefTarget(groupItem, isSelected)} onClick={() => {
          variationAnalyticsClickTracking(groupItem.seoLink, groupItem.variationText);
        }} aria-current={isSelected ? "page" : "false"} className={`${getTextClass(isSelected, groupItem.isInStock)} leading-4 p-2 flex relative items-center`}>
              <span className={`flex-1 break-words hover:underline ${isSelected ? "max-w-[90%]" : ""}`}>
                {groupItem.variationText}
              </span>

              {!groupItem.isInStock && !isSelected && <span className="text-gray-600 font-normal pl-3" data-testid="variationItemOutOfStock">
                  Out of stock
                </span>}
              {isSelected && <Icon name="ri-check-line" theme="wss" className="fill-white w-6 h-6 ml-auto absolute right-2" />}
            </a>
          </li>}
      </>;
  };
  const isQuantityGroup = (group: VariationGroup) => {
    return group.optionName === "Quantity";
  };
  const shouldDisplayAsTile = (group: VariationGroup) => group.displayTypeId === DisplayTypes.List && !group.variationGroupItems.some(item => item.variationText.length >= 18);
  function hasInvalidVariationTextForDivideByQuantityPrice(variations: VariationGroupItem[]) {
    return variations.some(variation => !variation.variationText.includes("/") && !variation.variationText.toLowerCase().includes("each"));
  }

  /* This function gets the divide by quantity price that shows under the main pricing */
  function getDivideByQuantityAndPrice(data: VariationGroupItem[], itemNumber: string, user: User) {
    const variationGroupItemUoms = data.map(x => x.variationText.split("/")[1]);
    const sameUoms = variationGroupItemUoms.every(unit => unit?.toLowerCase() === variationGroupItemUoms[0]?.toLowerCase());

    // Display the divide by price only if the units of measurement are not the same across all items
    const showDivideByPrice = !sameUoms;

    /* This is required for now since the data comes as a single text string instead of separate quantity and unit fields, which would be ideal */
    const itemsWithQty = data.map((item: VariationGroupItem) => {
      const variationTextSplit = item.variationText.split("/");
      let quantity = 1;
      let unit = "";
      if (variationTextSplit.length === 2) {
        const [quantityString, unitString] = variationTextSplit;
        quantity = Number(quantityString?.replace(/,/g, ""));
        unit = unitString ?? "";
      }
      if (variationTextSplit.length === 1 && variationTextSplit[0]?.toLowerCase() === "each") {
        quantity = 1;
        unit = "Each";
      }
      return {
        quantityPerPackaging: quantity,
        price: showDivideByPrice ? item.productPrice : null,
        unitOfMeasurement: showDivideByPrice ? unit : "",
        itemNumber: item.itemNumber,
        showBelowMinimumAdvertisedPriceForLoggedInCustomers: item.showBelowMinimumAdvertisedPriceForLoggedInCustomers,
        showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: item.showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers
      };
    });
    return getDivideByQuantityPricePerPackagingItem(itemsWithQty, itemNumber, user) ?? {
      itemNumber: "",
      divideByLowestQuantity: 0,
      minUnit: ""
    };
  }
  return <>
      {variationMembership?.variationGroups.map(group => {
      let {
        displayTypeId
      } = group;
      if (shouldDisplayAsTile(group)) {
        displayTypeId = DisplayTypes.Tile;
      }
      return <div id="productVariationBlock" key={`${group.id ?? "0"}.${group.optionName}`}>
            {!displayTypeId || displayTypeId === DisplayTypes.List || isQuantityGroup(group) ? <>
                {variationHeading(group, lowerCaseItemNumber, activeItemNumber)}
                <div id="ProductVariations" data-testid="ProductVariations" className={`${!isQuantityGroup(group) ? "variation-list" : ""}`}>
                  <ul className={cls("list-none", "m-0", getListClass(group))} aria-labelledby="productVariationsHeading" data-testid={isQuantityGroup(group) ? "available-quantities" : "variation-ul"}>
                    {group.variationGroupItems.sort((a, b) => sortGroupItems(group, a, b)).map(groupItem => getNonThumbnailVariationItem(groupItem, groupItem.itemNumber.toLowerCase() === lowerCaseItemNumber, isQuantityGroup(group), hasInvalidVariationTextForDivideByQuantityPrice(group.variationGroupItems) ? {
                itemNumber: "",
                divideByLowestQuantity: 0,
                minUnit: ""
              } : getDivideByQuantityAndPrice(group.variationGroupItems, groupItem.itemNumber, user)?.[0] ?? {
                itemNumber: "",
                divideByLowestQuantity: 0,
                minUnit: ""
              }, setActiveItemNumber, user))}
                  </ul>
                </div>
              </> : <ThumbnailVariation key={`${group.id ?? "0"}.${group.optionName}`} group={group} lowerCaseItemNumber={lowerCaseItemNumber} getHrefTarget={getHrefTarget} sortGroupItems={sortGroupItems} displayTypeId={displayTypeId} />}
          </div>;
    })}
    </>;
};
export default withUserAndChat(ProductVariations);